/**
===================================================
Module: frontend.src.components.SessionCard.tsx
Description: Document card component for displaying individual documents
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Session, isSessionActive } from "../types";
import SessionDetails from "./SessionDetails";
import { apiService } from "../services/api";
import "../styles/SessionDetails.css";

interface SessionCardProps {
  session: Session;
  onClick: (sessionId: string) => void;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (sessionId: string, isSelected: boolean) => void;
  onUpdateSession?: (
    sessionId: string,
    updates: { name: string; description: string },
  ) => Promise<void>;
  retentionDays?: number;
  expires_at?: string;
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  onUpdateSession,
  retentionDays = 30,
  expires_at: expiresAtProp,
}) => {
  const { t } = useTranslation('sessions');
  const [showDetails, setShowDetails] = useState(false);
  const [localEditingStatus, setLocalEditingStatus] = useState<string | null>(session.editing_status || null);
  const [thumbnailDimensions, setThumbnailDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Calculate days remaining
  const getDaysRemaining = () => {
    const created = new Date(session.created_at);
    const now = new Date();

    // Ensure now is at least created_at to avoid showing more days than the limit 
    // due to clock desync or immediate creation
    const effectiveNow = now < created ? created : now;

    // Use prop override if available, otherwise use session value
    const effectiveExpiresAt = expiresAtProp || session.expires_at;

    // If we have an explicit expiration date, use it directly
    if (effectiveExpiresAt) {
      const expires = new Date(effectiveExpiresAt);
      const diff = expires.getTime() - effectiveNow.getTime();
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    }

    // Per-session retention takes priority over account-level
    const effectiveRetention = session.retention_days ?? retentionDays;

    // Otherwise calculate based on retention days
    const expires = new Date(created.getTime() + effectiveRetention * 24 * 60 * 60 * 1000);
    const diff = expires.getTime() - effectiveNow.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    // Clamp to effectiveRetention to avoid rounding/desync
    return Math.max(0, Math.min(days, effectiveRetention));
  };

  const daysRemaining = getDaysRemaining();

  const getRetentionColor = (days: number) => {
    if (session.status === 'expired' || days <= 0) return '#ef4444'; // Red
    if (days <= 5) return '#eab308'; // Yellow
    return '#22c55e'; // Green
  };

  const [displayProgress, setDisplayProgress] = useState(session.progress || 0);
  const [showProgressBar, setShowProgressBar] = useState(session.status === "processing");
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    if (session.status === "processing") {
      setShowProgressBar(true);
      setDisplayProgress(session.progress || 0);
    } else if (session.status === "completed" && showProgressBar) {
      setDisplayProgress(100);
      transitionTimeoutRef.current = setTimeout(() => {
        setShowProgressBar(false);
      }, 1000);
    } else {
      setShowProgressBar(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [session.status, session.progress]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "status-completed";
      case "failed":
        return "status-failed";
      case "in_progress":
      case "processing":
        return "status-processing";
      case "queued":
        return "status-queued";
      case "uploading":
        return "status-uploading";
      case "waiting_for_upload":
        return "status-waiting";
      case "cancelled":
        return "status-cancelled";
      case "expired":
        return "status-expired";
      default:
        return "status-draft";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return t('sessionCard.status.completed');
      case "failed":
        return t('sessionCard.status.failed');
      case "in_progress":
      case "processing":
        return t('sessionCard.status.processing');
      case "queued":
        return t('sessionCard.status.queued');
      case "uploading":
        return t('sessionCard.status.uploading');
      case "waiting_for_upload":
        return t('sessionCard.status.waitingForUpload');
      case "cancelled":
        return t('sessionCard.status.cancelled');
      case "expired":
        return t('sessionCard.status.expired');
      default:
        return t('sessionCard.status.draft');
    }
  };

  const hasThumbnail =
    session.thumbnail_data && session.thumbnail_data.length > 0;

  const getThumbnailUrl = () => {
    if (!hasThumbnail) return null;

    if (
      typeof session.thumbnail_data === "string" &&
      session.thumbnail_data.startsWith("data:")
    ) {
      return session.thumbnail_data;
    }

    return null;
  };

  const thumbnailUrl = getThumbnailUrl();

  const handleThumbnailLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setThumbnailDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  const getThumbnailStyle = () => {
    if (!thumbnailDimensions) {
      return {
        width: '100%',
        height: '240px',
        objectFit: 'cover' as const,
      };
    }

    const containerWidth = 200;
    const containerHeight = 200;

    const aspectRatio = thumbnailDimensions.width / thumbnailDimensions.height;

    // Calculate dimensions that fit within container while maintaining aspect ratio
    let displayWidth = containerWidth;
    let displayHeight = containerWidth / aspectRatio;

    if (displayHeight > containerHeight) {
      displayHeight = containerHeight;
      displayWidth = containerHeight * aspectRatio;
    }

    return {
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      objectFit: 'contain' as const,
      maxWidth: '100%',
      maxHeight: '100%',
    };
  };

  const isActive = isSessionActive(session.status);
  const isExpired = session.status === 'expired';
  const isFailed = session.status === 'failed';
  const isCancelled = session.status === 'cancelled';

  // Any status that is not 'completed' is considered "unopenable" for now,
  // as it doesn't have results to view or editor content ready.
  const isUnopenable = session.status !== 'completed';

  const handleCardClick = () => {
    if (isSelectionMode) {
      onSelect?.(session.id, !isSelected);
      return;
    }

    // Block opening if unopenable
    if (isUnopenable) return;

    if (session.status === "completed") {
      setShowDetails(true);
    } else {
      // This part might be unreachable now if isUnopenable blocks everything else,
      // but we'll keep the logic clean.
      onClick(session.id);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(session.id, !isSelected);
  };

  const handleUpdateSession = async (sessionId: string, updates: { name: string; description: string }) => {
    if (onUpdateSession) {
      await onUpdateSession(sessionId, updates);
    }
  };

  const handleEditingStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const newStatus = e.target.value || null;
    setLocalEditingStatus(newStatus);
    try {
      await apiService.updateSessionDetails(session.id, {
        name: session.name,
        description: session.description || '',
        editing_status: newStatus,
      } as any);
    } catch (err) {
      console.error('Failed to update editing status:', err);
      setLocalEditingStatus(session.editing_status || null);
    }
  };

  return (
    <>
      <div
        className={`session-card ${isSelected ? "selected" : ""} ${isSelectionMode ? "selection-mode" : ""} ${isUnopenable ? "unopenable" : ""}`}
        onClick={handleCardClick}
      >
        {isSelectionMode && (
          <div className="selection-checkbox" onClick={handleCheckboxClick}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => { }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className="session-thumbnail">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={session.name}
              className="thumbnail-image"
              style={getThumbnailStyle()}
              onLoad={handleThumbnailLoad}
            />
          ) : (
            <div className="thumbnail-placeholder">📄</div>
          )}

          {/* Retention Badge */}
          <div className="retention-badge">
            {session.retention_days === 0 ? (
              <>
                <div
                  className="retention-dot"
                  style={{ backgroundColor: '#3b82f6' }}
                />
                {t('sessionCard.deleteAfterDL', { defaultValue: 'Delete after DL' })}
              </>
            ) : (
              <>
                <div
                  className="retention-dot"
                  style={{ backgroundColor: getRetentionColor(daysRemaining) }}
                />
                {t('daysLeft', { count: daysRemaining })}
              </>
            )}
          </div>
        </div>

        <div className="session-info">
          <div className="session-header">
            <h3 className="session-name">
              {session.name || t('sessionCard.untitled')}
            </h3>
            <span className={`status-badge ${getStatusClass(session.status)}`}>
              {getStatusText(session.status)}
              {session.queue_position != null && (
                <span className="queue-position"> ({session.queue_position})</span>
              )}
            </span>
          </div>

          <div className="session-card-main-content">
            {showProgressBar && (
              <div className="session-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${displayProgress}%` }}
                  />
                </div>
                <div className="progress-text">
                  {displayProgress.toFixed(1)}% {t('sessionCard.complete')}
                </div>
              </div>
            )}

            <div className="session-description">
              {session.description || t('sessionCard.noDescription')}
            </div>
          </div>

          <div className="session-meta">
            <div className="session-date">{formatDate(session.created_at)}</div>
            <div className="editing-status-row" onClick={(e) => e.stopPropagation()}>
              <span className="editing-status-label">{t('sessionCard.editingStatus', { defaultValue: 'Editing status' })}</span>
              <select
                className={`editing-status-select ${localEditingStatus || 'blank'}`}
                value={localEditingStatus || ''}
                onChange={handleEditingStatusChange}
              >
                <option value="">{''}</option>
                <option value="editing">{t('sessionCard.editing', { defaultValue: 'Editing' })}</option>
                <option value="edited">{t('sessionCard.edited', { defaultValue: 'Edited' })}</option>
              </select>
            </div>
          </div>


        </div>
      </div>

      {showDetails && session.status === "completed" && (
        <SessionDetails
          session={session}
          onClose={() => setShowDetails(false)}
          onUpdate={handleUpdateSession}
        />
      )}
    </>
  );
};

export default SessionCard;
